import { createStore } from "vuex";

// PERFORMANCE: Only load essential modules synchronously
// These are needed for candidate assessment flow and basic app functionality
import auth from "./modules/auth";
import tracking from "./modules/tracking";
import customHost from "./modules/custom-host-context";

// Track which lazy modules have been registered
const registeredModules = new Set<string>();

const store = createStore({
  state: {
    userLogged: false,
    loadingStatus: false,
    isReady: false,
  },
  mutations: {
    userLoggedStatus(state, value) {
      state.userLogged = value;
    },
    loadingStatus(state, status) {
      state.loadingStatus = status;
    },
    isReady(state, isReady) {
      state.isReady = isReady;
    },
  },
  actions: {
    userLoginWidthGoogle({ commit }, value) {
      console.log("STORE: userLoginWidthGoogle func..");
      commit("loadingStatus", true);
      // API > commit
      setTimeout(() => {
        commit("loadingStatus", false);
        commit("userLoggedStatus", value);
      }, 3000);
    },
    isReady({ commit }, isReady) {
      commit("isReady", isReady);
    },
    forceReady({ dispatch, rootState }) {
      // used for Hot Code Push
      if (rootState.isReady) {
        dispatch("isReady", false);
      }
      dispatch("isReady", true);
    },
  },
  modules: {
    // Only keep modules here that are needed on first paint
    auth,
    tracking,
    customHost,
  },
  getters: {
    getLoginStatus(state) {
      return state.userLogged;
    },
    getLoadingStatus(state) {
      return state.loadingStatus;
    },
    isReady(state) {
      return state.isReady;
    },
  },
});

// PERFORMANCE: Lazy-load admin-only modules when first needed
// This reduces initial bundle size by ~150-200KB for candidate assessment pages

type LazyModuleKey =
  | "google"
  | "microsoft"
  | "CreateAccountForm"
  | "routes"
  | "user"
  | "guest"
  | "fixtures"
  | "team"
  | "assessment"
  | "assessmentSection"
  | "assessmentTemplate"
  | "respondent"
  | "caches"
  | "big5"
  | "quota"
  | "payment"
  | "coupon"
  | "referral"
  | "aiProxy"
  | "aiService"
  | "coreValuesAnalyzer"
  | "toolbox"
  | "jobRoleGenerator"
  | "jobPostGenerator"
  | "careerPathTracker"
  | "okrGenerator"
  | "performanceImprovement"
  | "numDayPlan"
  | "interviewGuide"
  | "greenhouse"
  | "aiAssessmentTemplate"
  | "sampleData"
  | "publicAiAssessment"
  | "customDomain"
  | "resultsReport";

const lazyModuleLoaders: Record<LazyModuleKey, () => Promise<any>> = {
  // OAuth modules - only needed during login/signup
  google: () => import("./modules/google"),
  microsoft: () => import("./modules/microsoft"),
  CreateAccountForm: () => import("./forms/CreateAccount"),
  routes: () => import("./modules/routes"),
  user: () => import("./modules/user"),
  guest: () => import("./modules/guest"),
  fixtures: () => import("./setup/fixtures"),

  // Heavy core modules - defer until the route actually needs them
  team: () => import("./modules/team"),
  assessment: () => import("./modules/assessment"),
  assessmentSection: () => import("./modules/assessment-section"),
  assessmentTemplate: () => import("./modules/assessment-template"),
  respondent: () => import("./modules/respondent"),
  caches: () => import("./modules/caches"),
  big5: () => import("./modules/big5"),

  // Billing & Payment - admin only
  quota: () => import("./modules/quota"),
  payment: () => import("./modules/payment"),
  coupon: () => import("./modules/coupon"),
  referral: () => import("./modules/referral"),

  // AI Services - admin only
  aiProxy: () => import("./modules/ai-proxy"),
  aiService: () => import("./modules/ai-service"),

  // Core Values & Toolbox - admin only
  coreValuesAnalyzer: () => import("./modules/core-values-analyzer"),
  toolbox: () => import("./modules/toolbox"),

  // AI-powered generators - admin only
  jobRoleGenerator: () => import("./modules/job-role-generator"),
  jobPostGenerator: () => import("./modules/job-post-generator"),
  careerPathTracker: () => import("./modules/career-path-tracker"),
  okrGenerator: () => import("./modules/okr-generator"),
  performanceImprovement: () => import("./modules/performance-improvement"),
  numDayPlan: () => import("./modules/num-day-plan"),
  interviewGuide: () => import("./modules/interview-guide"),

  // Integrations & misc - admin only
  greenhouse: () => import("./modules/greenhouse"),
  aiAssessmentTemplate: () => import("./modules/ai-assessment-template"),
  sampleData: () => import("./sample-data/sample-data"),

  // Public AI Readiness Assessment
  publicAiAssessment: () => import("./modules/public-ai-assessment"),

  customDomain: () => import("./modules/custom-domain"),

  // Results report engine - admin-triggered generation, public read
  resultsReport: () => import("./modules/results-report"),
};

/**
 * Dynamically register a lazy-loaded Vuex module
 * Call this before dispatching actions or accessing getters from lazy modules
 */
export async function ensureModule(moduleName: LazyModuleKey): Promise<void> {
  // Check if module is already registered
  if (registeredModules.has(moduleName) || store.hasModule(moduleName)) {
    return; // Already registered
  }

  const loader = lazyModuleLoaders[moduleName];
  if (!loader) {
    console.warn(`Unknown lazy module: ${moduleName}`);
    return;
  }

  try {
    const moduleDefinition = await loader();
    // Double-check before registering to prevent duplicate namespace errors
    if (!store.hasModule(moduleName)) {
      store.registerModule(
        moduleName,
        moduleDefinition.default || moduleDefinition
      );
      registeredModules.add(moduleName);
    }
  } catch (error) {
    console.error(`Failed to load module ${moduleName}:`, error);
    throw error;
  }
}

/**
 * Start downloading module chunks without registering them, so a later
 * ensureModules() finds them already in flight or loaded.
 */
export function preloadModules(moduleNames: readonly LazyModuleKey[]): void {
  for (const name of moduleNames) {
    if (!isModuleRegistered(name)) {
      lazyModuleLoaders[name]?.().catch(() => undefined);
    }
  }
}

/**
 * Ensure multiple modules are loaded
 */
export async function ensureModules(
  moduleNames: LazyModuleKey[]
): Promise<void> {
  await Promise.all(moduleNames.map((name) => ensureModule(name)));
}

/**
 * Check if a module has been registered
 */
export function isModuleRegistered(moduleName: string): boolean {
  return registeredModules.has(moduleName) || store.hasModule(moduleName);
}

export default store;
